import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock, Spinner } from '../components/Spinner'
import { PageHeading } from '../components/PageHeader'
import { ReloginModal } from '../components/Onboarding/ReloginModal'
import { useAccounts } from '../context/AccountsContext'
import { useAuth } from '../context/AuthContext'
import { useAccountPermissions } from '../dashboard/useAccountPermissions'
import { AccountSelect, ConfirmDialog } from '../dynamicLists/shared'
import type { CrmAccount } from '../dynamicLists/types'
import { usePayoutsAccess } from '../hooks/usePayoutsAccess'
import { useToast } from '../library/useToast'
import { hasReconnectAccess } from '../permissions'
import { NotConnected, payoutsApi, type PayoutsApi } from './api'
import {
  HISTORY_DAYS,
  blockerOf,
  buildHistory,
  filterHistory,
  formatCents,
  matchesSearch,
  openUnconfirmed,
  sortRows,
  totalsOf,
  type Attempt,
  type HistoryEntry,
  type Platform,
  type PlatformRequest,
  type RowAccount,
  type RowData,
  type RowState,
  type StatusFilter,
} from './payoutsData'
import { RequestDialog, type RequestDialogProps } from './RequestDialog'
import '../dynamicLists/dynamicLists.css'
import '../trackingLinks/trackingLinks.css'
import './payouts.css'

/** Balances load a few Accounts at a time: each is a live call to OnlyFans or Fansly. */
const CONCURRENCY = 8
/** Accounts and payout requests are shown (and Accounts loaded) this many at a time. */
export const PAGE = 20
const DAY_MS = 24 * 60 * 60 * 1000

/** Payouts (CONTEXT.md "Payouts page"): only for logins with Statements at Full on some Account. */
export function PayoutsPage() {
  const access = usePayoutsAccess()
  const navigate = useNavigate()
  if (access === null) return null
  if (!access) return <Navigate to="/" replace />
  return (
    <PayoutsView
      api={payoutsApi}
      onOpenAccount={(id) => navigate(`/account/${encodeURIComponent(id)}/crm/statements`)}
      renderReconnect={(account, onDone) => <Reconnect account={account} onDone={onDone} />}
    />
  )
}

type HistoryState = { status: 'loading' } | { status: 'ready'; rows: PlatformRequest[] } | { status: 'error' }

interface ViewProps {
  api: PayoutsApi
  /** Opens the Account's own Statements (OnlyFans) / Wallet (Fansly) page. */
  onOpenAccount: (accountId: string) => void
  renderRequest?: (props: RequestDialogProps) => ReactNode
  renderReconnect?: (account: RowAccount, onDone: () => void) => ReactNode
  now?: () => number
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e ?? ''))

export function PayoutsView({ api, onOpenAccount, renderRequest = (p) => <RequestDialog {...p} />, renderReconnect, now = Date.now }: ViewProps) {
  const { t, i18n } = useTranslation('payouts')
  const lang = i18n.language
  const { say, node: toast } = useToast(5000)

  const [accounts, setAccounts] = useState<RowAccount[] | null>(null)
  const [accountsError, setAccountsError] = useState('')
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [histories, setHistories] = useState<Record<string, HistoryState>>({})
  const [attempts, setAttempts] = useState<Attempt[]>([])
  const [attemptsFailed, setAttemptsFailed] = useState(false)
  const [since, setSince] = useState(() => now() - HISTORY_DAYS * DAY_MS)

  const [platform, setPlatform] = useState<'all' | Platform>('all')
  const [search, setSearch] = useState('')
  // What the rows are filtered by: the search once typing pauses, so a half-typed
  // name never reveals (and loads) the Accounts it happens to match on the way.
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [historyAccount, setHistoryAccount] = useState('')
  const [shownCount, setShownCount] = useState(PAGE)
  const [historyCount, setHistoryCount] = useState(PAGE)

  const [warning, setWarning] = useState<{ account: RowAccount; data: RowData; entry: HistoryEntry } | null>(null)
  const [requesting, setRequesting] = useState<{ account: RowAccount; data: RowData } | null>(null)

  // A refresh starts a new generation: answers of the old one are dropped.
  const gen = useRef(0)
  // Accounts whose balances and requests were asked for in this generation; a revealed
  // Account is loaded once, CONCURRENCY at a time.
  const [started, setStarted] = useState<ReadonlySet<string>>(() => new Set())
  const queue = useRef<{ account: RowAccount; g: number }[]>([])
  const running = useRef(0)

  const loadAttempts = useCallback(async () => {
    try {
      setAttempts(await api.attempts())
      setAttemptsFailed(false)
    } catch {
      setAttemptsFailed(true)
    }
  }, [api])

  const loadAccount = useCallback(
    async (account: RowAccount, from: number, g: number) => {
      const id = account.account_id
      setRows((r) => ({ ...r, [id]: { status: 'loading' } }))
      setHistories((h) => ({ ...h, [id]: { status: 'loading' } }))
      const [balance, history] = await Promise.allSettled([api.balances(account), api.requests(account, from)])
      if (gen.current !== g) return
      setRows((r) => ({
        ...r,
        [id]:
          balance.status === 'fulfilled'
            ? { status: 'ready', data: balance.value }
            : balance.reason instanceof NotConnected
              ? { status: 'notConnected' }
              : { status: 'error', message: messageOf(balance.reason) },
      }))
      setHistories((h) => ({ ...h, [id]: history.status === 'fulfilled' ? { status: 'ready', rows: history.value } : { status: 'error' } }))
    },
    [api],
  )

  const sinceRef = useRef(since)
  const pump = useCallback(() => {
    while (running.current < CONCURRENCY && queue.current.length) {
      const job = queue.current.shift()!
      if (job.g !== gen.current) continue
      running.current += 1
      void loadAccount(job.account, sinceRef.current, job.g).finally(() => {
        running.current -= 1
        pump()
      })
    }
  }, [loadAccount])

  const loadAll = useCallback(async () => {
    const g = ++gen.current
    const from = now() - HISTORY_DAYS * DAY_MS
    sinceRef.current = from
    setSince(from)
    setAccountsError('')
    queue.current = []
    setStarted(new Set())
    setRows({})
    setHistories({})
    void loadAttempts()
    let list: RowAccount[]
    try {
      list = await api.accounts()
    } catch (e) {
      if (gen.current === g) setAccountsError(messageOf(e) || t('accountsError'))
      return
    }
    if (gen.current !== g) return
    setAccounts([...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
  }, [api, loadAttempts, now, t])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  // Accounts are revealed PAGE at a time, in name order; only revealed ones are loaded.
  const filtered = useMemo(
    () => (accounts ?? []).filter((a) => (platform === 'all' || a.platform === platform) && matchesSearch(a, query)),
    [accounts, platform, query],
  )
  const revealed = useMemo(() => filtered.slice(0, shownCount), [filtered, shownCount])
  useEffect(() => {
    const fresh = revealed.filter((a) => !started.has(a.account_id))
    if (!fresh.length) return
    setStarted((s) => new Set([...s, ...fresh.map((a) => a.account_id)]))
    queue.current.push(...fresh.map((account) => ({ account, g: gen.current })))
    pump()
  }, [revealed, started, pump])

  const changeFilter = (apply: () => void) => {
    apply()
    setShownCount(PAGE)
  }
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const reloadAccount = (account: RowAccount) => {
    void loadAttempts()
    void loadAccount(account, since, gen.current)
  }

  // ── Derived ──────────────────────────────────────────────────────────────
  const byId = useMemo(() => new Map((accounts ?? []).map((a) => [a.account_id, a])), [accounts])
  const shownRows = useMemo(
    () => sortRows(revealed.map((account) => ({ account, state: rows[account.account_id] ?? ({ status: 'loading' } as RowState) }))),
    [revealed, rows],
  )
  const startedAccounts = useMemo(() => (accounts ?? []).filter((a) => started.has(a.account_id)), [accounts, started])
  const totals = totalsOf(startedAccounts.map((a) => rows[a.account_id] ?? ({ status: 'loading' } as RowState)))
  const moreAccounts = filtered.length - revealed.length
  const hasFansly = (accounts ?? []).some((a) => a.platform === 'fansly')
  const hasOnlyFans = (accounts ?? []).some((a) => a.platform === 'onlyfans')

  const requests = useMemo(() => Object.values(histories).flatMap((h) => (h.status === 'ready' ? h.rows : [])), [histories])
  // Only the loaded Accounts' requests: an attempt elsewhere could not be matched yet.
  const loadedAttempts = useMemo(() => attempts.filter((a) => started.has(a.account_id)), [attempts, started])
  const history = useMemo(() => buildHistory(requests, loadedAttempts, since), [requests, loadedAttempts, since])
  const filteredHistory = filterHistory(history, status, historyAccount)
  const shownHistory = filteredHistory.slice(0, historyCount)
  const moreHistory = filteredHistory.length - shownHistory.length
  const historyMissing = startedAccounts.filter((a) => histories[a.account_id]?.status === 'error')
  const historyLoading = startedAccounts.some((a) => histories[a.account_id]?.status !== 'ready' && histories[a.account_id]?.status !== 'error')
  const methodNames = useMemo(() => {
    const out = new Map<string, { provider: string; ending: string }>()
    for (const [id, s] of Object.entries(rows)) {
      if (s.status === 'ready' && s.data.detail.platform === 'fansly') for (const m of s.data.detail.methods) out.set(`${id}:${m.id}`, m)
    }
    return out
  }, [rows])

  const startRequest = (account: RowAccount, data: RowData) => {
    const entry = openUnconfirmed(history, account.account_id)
    if (entry) setWarning({ account, data, entry })
    else setRequesting({ account, data })
  }

  const finished = (account: RowAccount, message: string) => {
    setRequesting(null)
    say(message)
    reloadAccount(account)
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const money = (cents: number | null, currency = 'USD') => formatCents(cents, currency, lang)
  const day = (ms: number) => new Date(ms).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })
  const time = (ms: number) => new Date(ms).toLocaleTimeString(lang, { hour: 'numeric', minute: '2-digit' })
  const minutesAgo = (ms: number) => Math.max(1, Math.round((now() - ms) / 60000))

  const accountCell = (a: RowAccount) => (
    <span className="po-account">
      {a.avatar_url ? <img src={a.avatar_url} alt="" className="dl-avatar po-avatar" /> : <span className="dl-avatar dl-avatar--blank po-avatar" />}
      <span className="po-account-text">
        <span className="dl-strong dl-ellipsis">{a.name}</span>
        <span className="tl-sub">
          {a.username && <span>@{a.username}</span>}
          <span className={`dl-badge dl-badge--quiet po-platform is-${a.platform}`}>{t(`platform.${a.platform}`)}</span>
        </span>
      </span>
    </span>
  )

  const action = (account: RowAccount, state: RowState) => {
    if (state.status === 'loading') return <Spinner size={16} label={t('loading')} />
    if (state.status === 'notConnected') {
      return (
        <span className="po-action">
          <span className="dl-badge dl-badge--warn">{t('row.notConnected')}</span>
          {renderReconnect?.(account, () => reloadAccount(account))}
        </span>
      )
    }
    if (state.status === 'error') {
      return (
        <span className="po-action">
          <span className="dl-error po-row-error" title={state.message}>
            {t('row.failed')}
          </span>
          <button type="button" className="dl-link" onClick={() => reloadAccount(account)}>
            {t('retry')}
          </button>
        </span>
      )
    }
    const blocker = blockerOf(state.data)
    if (!blocker) {
      return (
        <button type="button" className="dl-btn dl-btn--primary po-request" onClick={() => startRequest(account, state.data)}>
          <FeatherIcon name="arrow-up-right" size={14} /> {t('row.request')}
        </button>
      )
    }
    const fixedOnPage = blocker === 'automatic' || blocker === 'noMethod' || blocker === 'taxLocked'
    return (
      <span className="po-action">
        <span className="dl-muted po-blocker">{t(`blocker.${blocker}`)}</span>
        {fixedOnPage && (
          <button type="button" className="dl-link" onClick={() => onOpenAccount(account.account_id)}>
            {t(account.platform === 'fansly' ? 'row.openWallet' : 'row.openStatements')}
          </button>
        )}
      </span>
    )
  }

  const statusCell = (h: HistoryEntry) => (
    <span className="po-status-cell">
      <span className={`po-status is-${h.status}`}>{t(`status.${h.status}`)}</span>
      {h.status === 'unconfirmed' && <span className="tl-sub">{t('status.unconfirmedHint')}</span>}
      {h.rejectReason && <span className="tl-sub">{h.rejectReason}</span>}
    </span>
  )

  const methodCell = (h: HistoryEntry) => {
    if (h.platform !== 'fansly' || !h.methodId) return <span className="dl-muted">—</span>
    const m = methodNames.get(`${h.accountId}:${h.methodId}`)
    if (!m) return <span className="dl-muted">—</span>
    const name = t(`fanslyWallet:methods.providers.${m.provider}`)
    return <span>{m.ending ? `${name} · ${t('fanslyWallet:methods.ending', { digits: m.ending })}` : name}</span>
  }

  const accountOptions: CrmAccount[] = useMemo(
    () => startedAccounts.map((a) => ({ account_id: a.account_id, profile_name: a.name, username: a.username, avatar_url: a.avatar_url, platform: a.platform, is_enabled: true })),
    [startedAccounts],
  )

  const kpis = (
    <section className="tl-totals po-totals" aria-label={t('totals.label')} aria-busy={totals.loading > 0}>
      <div className="tl-kpi">
        <span className="dl-label">{t('totals.available')}</span>
        <strong className="tl-kpi-value" data-testid="po-total-available">
          {money(totals.available)}
          {totals.loading > 0 && <Spinner size={12} label={t('loading')} />}
        </strong>
      </div>
      <div className="tl-kpi">
        <span className="dl-label">{t('totals.pending')}</span>
        <strong className="tl-kpi-value" data-testid="po-total-pending">
          {money(totals.pending)}
        </strong>
      </div>
      <div className="tl-kpi po-kpi-loaded">
        <span className="dl-label">{t('totals.accounts')}</span>
        <strong className="tl-kpi-value">{t('totals.counted', { count: totals.counted, total: (accounts ?? []).length })}</strong>
      </div>
    </section>
  )
  const totalsNote = [
    totals.notConnected > 0 && t('totals.notConnected', { count: totals.notConnected }),
    totals.failed > 0 && t('totals.failed', { count: totals.failed }),
  ].filter(Boolean)

  return (
    <div className="mobile-page-shell cpt-root dl-page tl-page po-page w-full px-6 pt-5 pb-6 min-w-0">
      <div className="po-head">
        <PageHeading title={t('title')} subtitle={t('subtitle')} />
        <button type="button" className="dl-btn po-refresh" onClick={() => void loadAll()} disabled={!accounts && !accountsError} aria-label={t('refresh')} title={t('refresh')}>
          <FeatherIcon name="refresh-cw" size={14} /> <span className="po-refresh-label">{t('refresh')}</span>
        </button>
      </div>

      {accountsError ? (
        <p className="dl-error">
          {accountsError}{' '}
          <button type="button" className="dl-link" onClick={() => void loadAll()}>
            {t('retry')}
          </button>
        </p>
      ) : !accounts ? (
        <LoadingBlock label={t('loading')} />
      ) : !accounts.length ? (
        <div className="dl-empty">
          <p className="dl-strong">{t('empty.title')}</p>
          <p className="dl-muted">{t('empty.body')}</p>
        </div>
      ) : (
        <>
          {kpis}
          {totalsNote.length > 0 && <p className="tl-kpi-note tl-totals-note po-totals-note">{totalsNote.join(' · ')}</p>}

          <div className="dl-bar tl-tools">
            <label className="dl-search tl-search">
              <FeatherIcon name="search" size={14} />
              <input value={search} onChange={(e) => changeFilter(() => setSearch(e.target.value))} placeholder={t('search')} aria-label={t('search')} />
            </label>
            {hasFansly && hasOnlyFans && (
              <div className="dl-seg" role="group" aria-label={t('platform.label')}>
                {(['all', 'onlyfans', 'fansly'] as const).map((p) => (
                  <button key={p} type="button" className={`dl-seg-btn ${platform === p ? 'is-on' : ''}`} aria-pressed={platform === p} onClick={() => changeFilter(() => setPlatform(p))}>
                    {t(`platform.${p}`)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="tl-table-wrap">
            <div className="tl-table-scroll">
              <table className="tl-table po-table po-table--accounts">
                <thead>
                  <tr>
                    <th>{t('col.account')}</th>
                    <th className="po-num">{t('col.available')}</th>
                    <th className="po-num">{t('col.pending')}</th>
                    <th>
                      <span className="sr-only">{t('col.action')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shownRows.map(({ account, state }) => (
                    <tr key={account.account_id} data-testid={`po-row-${account.account_id}`}>
                      <td className="po-cell-account">{accountCell(account)}</td>
                      <td className="po-num dl-strong" data-label={t('col.available')}>{state.status === 'ready' ? money(state.data.available, state.data.currency) : state.status === 'loading' ? '…' : '—'}</td>
                      <td className="po-num" data-label={t('col.pending')}>{state.status === 'ready' ? money(state.data.pending, state.data.currency) : state.status === 'loading' ? '…' : '—'}</td>
                      <td className="po-action-cell">{action(account, state)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!shownRows.length && <p className="dl-muted po-pad">{t('noMatch')}</p>}
            </div>
            {moreAccounts > 0 && (
              <button type="button" className="dl-btn tl-more" onClick={() => setShownCount((n) => n + PAGE)}>
                {t('showMoreAccounts', { count: Math.min(PAGE, moreAccounts), left: moreAccounts })}
              </button>
            )}
          </div>

          <section className="po-history" aria-labelledby="po-history-title">
            <div className="po-history-head">
              <div>
                <h2 id="po-history-title" className="po-h2">
                  {t('history.title')}
                </h2>
                <span className="dl-muted">{t('history.subtitle', { days: HISTORY_DAYS, count: startedAccounts.length })}</span>
              </div>
              <div className="dl-bar po-history-filters">
                <select className="dl-select po-status-select" value={status} onChange={(e) => {
                    setStatus(e.target.value as StatusFilter)
                    setHistoryCount(PAGE)
                  }} aria-label={t('history.status')}>
                  {(['all', 'processing', 'done', 'rejected', 'canceled', 'unconfirmed'] as const).map((s) => (
                    <option key={s} value={s}>
                      {s === 'all' ? t('history.allStatuses') : t(`status.${s}`)}
                    </option>
                  ))}
                </select>
                <AccountSelect
                  accounts={accountOptions}
                  value={historyAccount}
                  onChange={(id) => {
                    setHistoryAccount(id)
                    setHistoryCount(PAGE)
                  }}
                />
              </div>
            </div>
            {historyMissing.length > 0 && <p className="dl-notice dl-notice--warn">{t('history.missing', { names: historyMissing.map((a) => a.name).join(', ') })}</p>}
            {attemptsFailed && <p className="dl-notice dl-notice--warn">{t('history.attributionFailed')}</p>}
            {shownHistory.length > 0 ? (
              <div className="tl-table-scroll">
                <table className="tl-table po-table po-table--history">
                  <thead>
                    <tr>
                      <th>{t('col.date')}</th>
                      <th>{t('col.account')}</th>
                      <th className="po-num">{t('col.amount')}</th>
                      <th>{t('col.method')}</th>
                      <th>{t('col.status')}</th>
                      <th>{t('col.requestedBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownHistory.map((h) => {
                      const a = byId.get(h.accountId)
                      return (
                        <tr key={h.key} className={h.status === 'unconfirmed' ? 'po-unconfirmed' : undefined}>
                          <td className="po-h-date">
                            <span className="po-date">
                              <span>{day(h.createdAt)}</span>
                              <span className="tl-sub">{time(h.createdAt)}</span>
                            </span>
                          </td>
                          <td className="po-cell-account po-h-account">
                            {a ? (
                              <button type="button" className="po-account-link" onClick={() => onOpenAccount(a.account_id)} title={t(a.platform === 'fansly' ? 'row.openWallet' : 'row.openStatements')}>
                                {accountCell(a)}
                              </button>
                            ) : (
                              h.accountId
                            )}
                          </td>
                          <td className="po-num dl-strong po-h-amount">{money(h.amount, h.currency)}</td>
                          <td className={`po-h-method ${h.platform === 'fansly' && h.methodId ? '' : 'is-empty'}`}>{methodCell(h)}</td>
                          <td className="po-h-status">{statusCell(h)}</td>
                          <td className="po-h-by" data-label={h.requestedBy ? t('col.requestedBy') : undefined}>{h.requestedBy ? <span>{h.requestedBy}</span> : <span className="dl-muted">{t('history.outside')}</span>}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : historyLoading ? (
              <LoadingBlock label={t('loading')} />
            ) : (
              <p className="dl-muted po-pad">{history.length ? t('noMatch') : t('history.empty', { days: HISTORY_DAYS })}</p>
            )}
            {moreHistory > 0 && (
              <button type="button" className="dl-btn tl-more" onClick={() => setHistoryCount((n) => n + PAGE)}>
                {t('history.showMore', { count: Math.min(PAGE, moreHistory), left: moreHistory })}
              </button>
            )}
            <p className="dl-muted po-history-foot">{t('history.older')}</p>
          </section>
        </>
      )}

      {warning && (
        <ConfirmDialog
          title={t('warning.title')}
          confirm={t('warning.continue')}
          onClose={() => setWarning(null)}
          onConfirm={async () => {
            const { account, data } = warning
            setWarning(null)
            setRequesting({ account, data })
          }}
        >
          <p>{t('warning.body', { amount: money(warning.entry.amount, warning.entry.currency), minutes: minutesAgo(warning.entry.createdAt) })}</p>
        </ConfirmDialog>
      )}
      {requesting &&
        renderRequest({
          account: requesting.account,
          data: requesting.data,
          onClose: () => setRequesting(null),
          onFinished: (message) => finished(requesting.account, message),
        })}
      {toast && <div className="lib po-dialog-host">{toast}</div>}
    </div>
  )
}

/** Log in again for a Not connected row, for logins allowed to reconnect (as the CRM's logged-out banner). */
function Reconnect({ account, onDone }: { account: RowAccount; onDone: () => void }) {
  const { t } = useTranslation('payouts')
  const { isSuperAdmin } = useAuth()
  const { accounts, refresh } = useAccounts()
  const consoleAccount = useMemo(() => accounts.find((a) => a.account_id === account.account_id), [accounts, account.account_id])
  const scoped = useMemo(() => (consoleAccount ? [consoleAccount] : []), [consoleAccount])
  const perms = useAccountPermissions(scoped)
  const [open, setOpen] = useState(false)
  const allowed = !!consoleAccount && (isSuperAdmin || hasReconnectAccess(perms.getPermissions(account.account_id)))
  if (!allowed) return <span className="dl-muted po-blocker">{t('row.askOwner')}</span>
  return (
    <>
      <button type="button" className="dl-link" onClick={() => setOpen(true)}>
        {t('row.reconnect')}
      </button>
      {open && (
        <ReloginModal
          accountId={account.account_id}
          platform={account.platform}
          onClose={() => setOpen(false)}
          onConnected={() => {
            setOpen(false)
            void refresh()
            onDone()
          }}
        />
      )}
    </>
  )
}
